import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { CategoryAttribute, ItemAttribute } from '../../../types/interfaces'
import { FieldType, ValueType } from '../../../types/enums'
import {
  maxNumberValueLength,
  maxOptionsTextFieldLength,
  validNumber,
} from '../../../utils/constants'

export const useForm = <T extends { attributes: ItemAttribute[] }>(
  setTopData: React.Dispatch<React.SetStateAction<T>>,
  topData: T,
  setDisabled: React.Dispatch<SetStateAction<boolean>>,
  categoryAttributes?: CategoryAttribute[],
  itemAttributes?: ItemAttribute[],
) => {
  const [data, setData] = useState<ItemAttribute[]>([])
  const prevCategoryAttributesRef = useRef<CategoryAttribute[]>([])
  const prevItemAttributesRef = useRef<ItemAttribute[]>([])

  const getDefaultValue = (valueType: ValueType): string | boolean | string[] => {
    switch (valueType) {
      case ValueType.NUMBER:
        return ''
      case ValueType.BOOLEAN:
        return false
      case ValueType.DATE:
        return ''
      case ValueType.STRING:
      default:
        return ''
    }
  }
  const sort = (a: ItemAttribute, b: ItemAttribute) => {
    return a.sortOrder - b.sortOrder
  }
  const prepareProductAttributes = useCallback(
    (
      categoryAttributes: CategoryAttribute[],
      existingProductAttributes: ItemAttribute[] = [],
    ): ItemAttribute[] => {
      const existingMap = new Map(existingProductAttributes.map((attr) => [attr.externalId, attr]))
      return categoryAttributes.map((catAttr) => {
        const existing = existingMap.get(catAttr.externalId)
        if (existing) {
          return existing
        }
        const { id: _, ...catAttrWithoutId } = catAttr
        return {
          id: `temp_${catAttr.externalId}`,
          ...catAttrWithoutId,
          value: getDefaultValue(catAttr.valueType),
          categoryAttributeId: catAttr.id,
        } as ItemAttribute
      })
    },
    [],
  )
  useEffect(() => {
    if (topData.attributes.length && !data.length) {
      setData(topData.attributes)
      return
    }

    if (!categoryAttributes || categoryAttributes.length === 0) {
      if (data.length > 0) setData([])
      return
    }

    if (!data.length) {
      const catAttrsChanged =
        !prevCategoryAttributesRef.current ||
        JSON.stringify(prevCategoryAttributesRef.current) !== JSON.stringify(categoryAttributes)
      const itemAttrsChanged =
        !prevItemAttributesRef.current ||
        JSON.stringify(prevItemAttributesRef.current) !== JSON.stringify(itemAttributes)
      if (catAttrsChanged || itemAttrsChanged) {
        const newData = prepareProductAttributes(categoryAttributes, itemAttributes || [])
        prevCategoryAttributesRef.current = categoryAttributes
        if (itemAttributes) prevItemAttributesRef.current = itemAttributes

        setData(newData)
      }
    }
  }, [categoryAttributes, itemAttributes, prepareProductAttributes, data.length, topData])

  const handleAttributeChange = useCallback((id: string, value: string | string[] | boolean) => {
    setData((prev) =>
      prev.map((attr) => {
        if (String(attr.id) !== String(id)) return attr
        if (attr.valueType === ValueType.NUMBER && typeof value === 'string') {
          if (value.startsWith('0')) return attr
          if (!validNumber.test(value)) return attr
          if (value.length > maxNumberValueLength) return attr
        }
        return { ...attr, value }
      }),
    )
  }, [])

  useEffect(() => {
    const hasEmptyRequiredField = data.some(
      (el) =>
        el.isRequired &&
        (el.value === '' ||
          el.value === false ||
          (Array.isArray(el.value) && el.value.length === 0)),
    )

    const hasTooLongText = data.some(
      (el) =>
        el.fieldType === FieldType.INPUT && String(el.value).length > maxOptionsTextFieldLength,
    )

    setDisabled(hasEmptyRequiredField || hasTooLongText)
  }, [data, setDisabled])

  useEffect(() => {
    setTopData((prev) => ({
      ...prev,
      attributes: [...data],
    }))
  }, [data, setTopData])

  return { data, handleAttributeChange, sort }
}
